import { Parser } from "acorn";
import { simple } from "acorn-walk";
// NOTE: must be the named export. The default export is not callable here, so
// `tsPlugin()` threw on every call — swallowed by the catch below, which made
// getClassMethods() return an empty list for ANY input. That silently emptied
// the function list in the converter editor as well.
import { tsPlugin } from "acorn-typescript";

// AST mit Acorn + TypeScript-Plugin erstellen
const getAst = (code: string) => {
    try {
        return Parser.extend(tsPlugin() as any).parse(code, {
            ecmaVersion: "latest",
            sourceType: "module",
        })
    } catch (err) {
        //console.error(err)
        return null
    }
}

/**
 * Is this a conversion function, i.e. does it take (args: ConverterProps)?
 *
 * The one place that decides it. A method without that exact annotation is
 * invisible to the mapping, so both readers below have to agree on the rule —
 * comparing a list of methods against a set of bodies gathered under a
 * different rule would produce phantom renames.
 */
const isConversionMethod = (node: any): boolean => {
    if (!node.value?.params?.length) return false

    const firstParam = node.value.params[0]
    if (firstParam.type !== "Identifier" || firstParam.name !== "args") return false

    const paramTypeAnnotation = firstParam.typeAnnotation?.typeAnnotation
    return paramTypeAnnotation?.type === "TSTypeReference" && paramTypeAnnotation.typeName.name === "ConverterProps"
}

/**
 * Parse the Code script and returns a list of all class methods with parameter args:ConverterProps
 * @param code 
 * @returns 
 */
export function getClassMethods(code: string) {
    const ast = getAst(code)
    const methodNames: string[] = []

    if (!ast) return []

    // AST durchsuchen
    simple(ast, {
        MethodDefinition(node: any) {
            if (isConversionMethod(node)) methodNames.push(node.key.name)
        }
    });

    // returns ["m1", "m2"] etc.
    //console.log(methodNames); 

    return methodNames
}

/**
 * The same methods as getClassMethods(), but with their bodies.
 *
 * Deliberately a SECOND function rather than a richer return value from
 * getClassMethods(): that one is called in four places, on the server as well,
 * and its shape must not move. Nobody who only wants the names should have to
 * learn about bodies.
 *
 * Bodies come back whitespace-normalised, because the only question asked of
 * them is "is this the same code under a different name" — and re-indenting on
 * save must not turn into a claim that the code changed. Comments are kept:
 * they are part of what the author wrote, and treating a changed comment as
 * "not the same function" only ever costs a suggestion, never a wrong one.
 *
 * @returns method name -> normalised body, `{}` braces included
 */
export function getClassMethodBodies(code: string): Record<string, string> {
    const ast = getAst(code)
    const bodies: Record<string, string> = {}

    if (!ast) return bodies

    simple(ast, {
        MethodDefinition(node: any) {
            if (!isConversionMethod(node)) return

            const body = node.value?.body
            if (typeof body?.start !== "number" || typeof body?.end !== "number") return

            bodies[node.key.name] = code.slice(body.start, body.end).replace(/\s+/g, " ").trim()
        }
    });

    return bodies
}